import React from "react";
import balance from "../../assets/icons/balance.png";
import m2 from "../../assets/icons/m2.png";
import trees from "../../assets/icons/trees.png";
import seaweed from "../../assets/icons/seaweed.png";
import biodiversity from "../../assets/icons/biodiversity.png";
import minutesOfFootage from "../../assets/icons/minutesOfFootage.png";
import imagesCollected from "../../assets/icons/imagesCollected.png";
import monitoringStation from "../../assets/icons/monitoringStation.png";
import { ProjectResponse } from "../impact-map/map-setup-context/InitialMapValues";
import { TransactionsResponse } from "../transaction-ledger/types";

export type OutcomeCardData = {
  unit: string;
  description: React.ReactElement;
  icon: string;
  // The marine descriptions are too long for the mobile card's side-by-side
  // split, so they stack the text under the icon instead.
  stacksOnMobile?: boolean;
};

export const outcomeDataMapper = (unit: string): OutcomeCardData | undefined =>
  [
    {
      unit: "Tonnes CO2",
      description: (
        <span>
          Tonnes of CO<sub>2</sub>
        </span>
      ),
      icon: balance,
    },
    {
      unit: "m2",
      description: (
        <span>
          m<sup>2</sup> of land supported
        </span>
      ),
      icon: m2,
    },
    {
      unit: "Tree(s)",
      description: <span>Trees</span>,
      icon: trees,
    },
    {
      unit: "m3 Kelp",
      description: (
        <span>
          m<sup>3</sup> of kelp
        </span>
      ),
      icon: seaweed,
    },
    {
      unit: "Biodiversity",
      description: <span>Biodiversity Credits</span>,
      icon: biodiversity,
    },
    {
      unit: "Minutes of monitoring footage",
      description: (
        <span>
          minutes of monitoring footage collected, processed, and analysed
        </span>
      ),
      icon: minutesOfFootage,
      stacksOnMobile: true,
    },
    {
      unit: "Monitoring images",
      description: (
        <span>seafloor images collected, processed, and analysed</span>
      ),
      icon: imagesCollected,
      stacksOnMobile: true,
    },
    {
      unit: "Monitoring stations",
      description: <span>monitoring stations monitored for a year</span>,
      icon: monitoringStation,
      stacksOnMobile: true,
    },
  ].find((outcome) => outcome.unit === unit);

export const formatValueToShort = (value: number) => {
  return Intl.NumberFormat("en-GB", {
    notation: "compact",
    maximumFractionDigits: 2,
  })
    .format(value)
    .toLowerCase();
};

export const newMethodologyCutoff = 1776207540000; // 15th April 2026

const hasM2WithDatePostCutoff = (project: ProjectResponse): boolean => {
  const { outcomes } = project;
  return [...outcomes.v2, ...outcomes.v3].some(
    (outcome) =>
      outcome.unit === "m2" &&
      outcome.startDate &&
      outcome.startDate > newMethodologyCutoff,
  );
};

export const needsM2Icon = (
  transactionLedgerList: TransactionsResponse,
  projects: Array<ProjectResponse>,
) => {
  const projectNamesWithM2Outcome = new Set(
    projects.filter(hasM2WithDatePostCutoff).map((project) => project.name),
  );

  const meetsCriteriaForIcon = transactionLedgerList.some(
    (investment) =>
      investment.createdAt > newMethodologyCutoff &&
      projectNamesWithM2Outcome.has(investment.projectName),
  );

  return meetsCriteriaForIcon;
};
